import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/sections/Footer';
import { ProductCard, Product } from '@/components/store/ProductCard';
import { useCart } from '@/contexts/CartContext';
import { SEO } from '@/components/SEO';
import { SkipLink } from '@/components/SkipLink';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { HtmlContent } from '@/components/ui/html-content';
import { stripHtml } from '@/lib/html-utils';
import { Search, Loader2 } from 'lucide-react';
import { fetchProducts, fetchCategories, mapWooCommerceProduct } from '@/lib/woocommerce';
import { useSearchParams } from 'react-router-dom';
import { trackSearch } from '@/lib/meta-pixel';

const Catalogo = () => {
  const { addToCart } = useCart();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [page, setPage] = useState(1);

  // Recupera categorie
  const { data: categories = [] } = useQuery({
    queryKey: ['woocommerce-categories'],
    queryFn: () => fetchCategories({ per_page: 100 }),
  });

  // Trova la categoria selezionata dall'URL o dal nome
  const selectedCategoryFromUrl = searchParams.get('categoria');
  const selectedCategory = useMemo(() => {
    if (!selectedCategoryFromUrl) return 'all';
    
    // Cerca la categoria per slug o nome (senza HTML)
    const category = categories.find(cat => {
      const catNamePlain = stripHtml(cat.name);
      const catSlugPlain = cat.slug;
      return catNamePlain === selectedCategoryFromUrl || 
             catSlugPlain === selectedCategoryFromUrl ||
             cat.name === selectedCategoryFromUrl;
    });
    
    return category ? category.name : 'all';
  }, [selectedCategoryFromUrl, categories]);

  // Trova l'ID della categoria selezionata
  const categoryId = useMemo(() => {
    if (selectedCategory === 'all') return undefined;
    const category = categories.find(cat => cat.name === selectedCategory);
    return category?.id;
  }, [selectedCategory, categories]);

  // Recupera prodotti
  const { data, isLoading, error } = useQuery({
    queryKey: ['woocommerce-products', page, categoryId, searchQuery],
    queryFn: () => fetchProducts({
      page,
      per_page: 20,
      category: categoryId,
      search: searchQuery || undefined,
      orderby: 'menu_order',
      order: 'asc',
    }),
  });

  const products: Product[] = useMemo(() => {
    if (!data?.products) return [];
    return data.products.map(mapWooCommerceProduct);
  }, [data]);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setPage(1);
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set('search', value);
      // Traccia ricerca Meta Pixel
      trackSearch(value);
    } else {
      newParams.delete('search');
    }
    setSearchParams(newParams);
  };

  const handleCategoryChange = (value: string | undefined) => {
    const categoryValue = value || 'all';
    setPage(1);
    const newParams = new URLSearchParams(searchParams);
    if (categoryValue !== 'all') {
      // Salva il nome della categoria (che può contenere HTML) nell'URL
      newParams.set('categoria', categoryValue);
    } else {
      newParams.delete('categoria');
    }
    setSearchParams(newParams);
  };

  return (
    <>
      <SkipLink />
      <main id="main-content" className="min-h-screen bg-background">
        <SEO
          title="Catalogo Prodotti - Brace e Sapori Store"
          description="Sfoglia il nostro catalogo completo di costate di alta qualità e prodotti tipici pugliesi. Filtra per categoria, cerca il tuo prodotto preferito. Spedizione in tutta Italia."
          keywords="catalogo prodotti pugliesi, costate online, prodotti tipici puglia, carne online, salumi pugliesi, formaggi puglia, e-commerce puglia, brace e sapori catalogo"
          ogImage="/logo.png"
        />
        <Header />
        
        <div className="pt-24 pb-16 px-4 md:px-6">
          <div className="mx-auto max-w-7xl">
            <div className="mb-8">
              <h1 className="mb-4 text-4xl font-bold">Catalogo Prodotti</h1>
              <p className="text-muted-foreground">
                Scopri la nostra selezione di costate e prodotti tipici pugliesi
              </p>
            </div>

            {/* Filtri e Ricerca */}
            <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Cerca prodotti..."
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select 
                value={selectedCategory === 'all' ? undefined : selectedCategory} 
                onValueChange={(value) => handleCategoryChange(value || 'all')}
              >
                <SelectTrigger className="w-full md:w-[200px]">
                  <SelectValue placeholder="Tutte le categorie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte le categorie</SelectItem>
                  {categories.map((category) => (
                    <SelectItem key={category.id} value={category.name}>
                      <HtmlContent html={category.name} /> ({category.count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Loading State */}
            {isLoading && (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <span className="ml-2 text-muted-foreground">Caricamento prodotti...</span>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="py-16 text-center">
                <p className="text-destructive">
                  Errore nel caricamento dei prodotti. Riprova più tardi.
                </p>
              </div>
            )}

            {/* Lista Prodotti */}
            {!isLoading && !error && (
              <>
                {products.length === 0 ? (
                  <div className="py-16 text-center">
                    <p className="text-muted-foreground">
                      Nessun prodotto trovato. Prova a modificare i filtri di ricerca.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                      {products.map((product) => (
                        <ProductCard
                          key={product.id}
                          product={product}
                          onAddToCart={addToCart}
                        />
                      ))}
                    </div>

                    {/* Paginazione */}
                    {data && data.totalPages > 1 && (
                      <div className="mt-8 flex items-center justify-center gap-2">
                        <button
                          onClick={() => setPage(p => Math.max(1, p - 1))}
                          disabled={page === 1}
                          className="rounded-md border border-input bg-background px-4 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground disabled:opacity-50 disabled:pointer-events-none"
                        >
                          Precedente
                        </button>
                        <span className="px-4 py-2 text-sm text-muted-foreground">
                          Pagina {page} di {data.totalPages}
                        </span>
                        <button
                          onClick={() => setPage(p => Math.min(data.totalPages, p + 1))}
                          disabled={page === data.totalPages}
                          className="rounded-md border border-input bg-background px-4 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground disabled:opacity-50 disabled:pointer-events-none"
                        >
                          Successiva
                        </button>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>

        <Footer />
      </main>
    </>
  );
};

export default Catalogo;
